import { Dispatch, ThunkAction, UnknownAction } from '@reduxjs/toolkit'
import { EthAddr, TxHash } from '@shared/SchemaTypes'
import { Contract, Signer } from 'ethers'
import { ActivityEnum, AlertLevelEnum, PoolTypeEnum, RoleEnum } from '@shared/Enums'
import { poolActions } from '@react/redux/poolSlice'
import { logOT } from '@shared/loggingShared'
import { IPoolBaseAbi, PoolControllerDynamicAbi, PoolDynamicAbi } from '@shared/Abis'
import { assertRole, dispatchError, OtStoreState } from '@react/redux/store'
import { otAssert } from '@shared/conversions'
import { alertActions } from '@react/redux/alertSlice'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import { txUUID, wsServerActionPoolTX, wsServerActionPoolTXRequest } from '@shared/WsServerActions'
import { PoolControllerDynamic, PoolDynamic } from '@typechain-types/index'
import { UserConnection } from '@shared/DeploymentConfigTypes'

export const dynamicSetIndicativeInterestRateTx =
  (props: {
    poolAddr: EthAddr
    indicativeInterestRate: string
    collateralRate: string
  }): ThunkAction<void, OtStoreState, unknown, UnknownAction> =>
  async (dispatch, getState): Promise<void> => {
    const { poolAddr, indicativeInterestRate, collateralRate } = props

    const activity = ActivityEnum.POOL_ACTIVATE
    try {
      logOT.debug(activity)
      const { webSocket } = getState().chainState
      const { chainConfigurationName } = getState().chainState.chainConfiguration
      const { roleEnum, signer } = getState().chainState.userConnection as UserConnection
      assertRole(roleEnum, [RoleEnum.POOL_ADMIN, RoleEnum.BORROWER])
      otAssert(signer, 'signer is not defined')

      await dynamicSetIndicativeInterestRate(
        webSocket,
        signer as Signer,
        poolAddr,
        indicativeInterestRate,
        collateralRate,
        chainConfigurationName
      )

      dispatch(poolActions.setIndicativeInterestRateSuccess({ poolAddr }))
    } catch (e: any) {
      dispatch(poolActions.setIndicativeInterestRateFail({}))
      dispatch(
        alertActions.addAlert({
          title: 'Could not set indicative interest rate.',
          description: 'Please wait and try again',
          createdAt: Date.now() / 1000,
          dueDateTime: Date.now() / 1000,
          txHash: '',
          sourceObjectAddr: ETH_ADDR_ZERO,
          alertLevel: AlertLevelEnum.ERROR,
        })
      )
      await dispatchError(dispatch, e, activity, 1037)
    }
  }
export const dynamicSetIndicativeInterestRateDirect = async (props: {
  poolAddr: EthAddr
  indicativeInterestRate: string
  collateralRate: string
  webSocket: WebSocket | undefined
  signer: Signer
  chainConfigurationName: string

  roleEnum: RoleEnum
  dispatch: Dispatch
}): Promise<void> => {
  const {
    poolAddr,
    indicativeInterestRate,
    collateralRate,
    webSocket,
    signer,
    chainConfigurationName,
    roleEnum,
    dispatch,
  } = props
  const activity = ActivityEnum.POOL_ACTIVATE
  try {
    logOT.debug(activity)
    assertRole(roleEnum, [RoleEnum.POOL_ADMIN, RoleEnum.BORROWER])
    otAssert(signer, 'signer is not defined')

    await dynamicSetIndicativeInterestRate(
      webSocket,
      signer as Signer,
      poolAddr,
      indicativeInterestRate,
      collateralRate,
      chainConfigurationName
    )

    dispatch(poolActions.setIndicativeInterestRateSuccess({ poolAddr }))
  } catch (e: any) {
    dispatch(poolActions.setIndicativeInterestRateFail({}))
    dispatch(
      alertActions.addAlert({
        title: 'Could not set indicative interest rate.',
        description: 'Please wait and try again',
        createdAt: Date.now() / 1000,
        dueDateTime: Date.now() / 1000,
        txHash: '',
        sourceObjectAddr: ETH_ADDR_ZERO,
        alertLevel: AlertLevelEnum.ERROR,
      })
    )
    dispatchError(dispatch, e, activity, 1037)
  }
}
const dynamicSetIndicativeInterestRate = async (
  webSocket: WebSocket | undefined,
  signer: Signer,
  poolAddr: EthAddr,
  indicativeInterestRate: string,
  collateralRate: string,
  chainConfigurationName: string
): Promise<void> => {
  logOT.debug('setIndicativeInterestRate')
  const uuid = txUUID()
  webSocket?.send(
    wsServerActionPoolTXRequest(ActivityEnum.EXCHANGE_RATE_UPDATE, poolAddr, uuid, chainConfigurationName)
  )

  const poolBaseContract = new Contract(poolAddr, IPoolBaseAbi, signer)
  const poolType = Number(await poolBaseContract.poolType())

  if (poolType === PoolTypeEnum.Dynamic) {
    const poolContract = new Contract(poolAddr, PoolDynamicAbi, signer) as unknown as PoolDynamic
    const poolControllerAddr = await poolContract.poolControllerDynamic()

    otAssert(poolControllerAddr)

    const poolControllerContract = new Contract(
      poolControllerAddr,
      PoolControllerDynamicAbi,
      signer
    ) as unknown as PoolControllerDynamic

    const tx2 = await poolControllerContract
      .connect(signer)
      .setIndicativeAndCollateralRates(indicativeInterestRate, collateralRate)

    webSocket?.send(
      wsServerActionPoolTX(ActivityEnum.POOL_ACTIVATE, poolAddr, uuid, tx2.hash as TxHash, chainConfigurationName)
    )

    await tx2.wait(1)
  } else {
    throw new Error('Unknown pool type')
  }
}
